import React, { useEffect, useState, useMemo } from "react";
import "./EducationForm.css";
import { educationService } from "../../services/Education.service";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { REACT_APP_API_BASE_URL } from "../../apiurl";
import MediaLibraryModal from "../../components/common/MediaLibrary/MediaLibraryModal";
import DefaultImage from "../../assets/Newspaper.jpg";


const EducationForm = ({ educationId, onSubmitted, onCancel }) => {
  const id = educationId;

  const [formData, setFormData] = useState({
    publisher: "",
    title: "",
    link: "",
    publication_date: "",
    publish_time: "",
    brief_description: "",
    education_content: "",
    tags_stocks: [],
    image_url: null,
    credit: "",
    source_url: "",
    destination_url: "",
    tags_sectors: [],
    tags_industries: [],
    education_category: "",
    ntf_type: "",
    premium: false,
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [internalTokens, setInternalTokens] = useState([]);
  const [tagSectors, setTagSectors] = useState([]);
  const [tagIndustries, setTagIndustries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isPublished, setIsPublished] = useState(false);
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false);
  const [notificationTypes, setNotificationTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [educationCategory, seteducationCategory] = useState("");
  const [fetchingEducation, setFetchingEducation] = useState(false);
  const [educationFetchError, setEducationFetchError] = useState(null);


  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      const file = files[0];
      if (file) {
        setFormData({ ...formData, image_url: file });
        setImagePreview(URL.createObjectURL(file));
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleMediaSelect = (imageName) => {
    setFormData({ ...formData, image_url: imageName });
    setImagePreview(`${REACT_APP_API_BASE_URL}uploads/educationimages/${imageName}`);
  };

  const handleEditorChange = (content) => {
    setFormData({ ...formData, education_content: content });
  };

  const handlePremiumChange = (e) => {
    setFormData({ ...formData, premium: e.target.checked });
  };

  const normalizeHtml = (html = "") =>
    String(html)
      .replace(/&nbsp;/g, " ")
      .replace(/\u00a0/g, " ")
      .trim();

  const fetchInternalTokens = async () => {
    try {
      const response = await educationService.getInternalTokens();
      setInternalTokens(response.data);
    } catch (error) {
      console.error("Error fetching internal tokens:", error);
    }
  };

  const fetchNotificationTypes = async () => {
    try {
      const response = await educationService.getAllNotificationTypes();
      setNotificationTypes(response || []);
    } catch (error) {
      console.error("Error fetching notification types:", error);
    }
  };

  const fetchTagSectors = async () => {
    try {
      const response = await educationService.getTagSectors();
      setTagSectors(response.data || []);
    } catch (error) {
      console.error("Error fetching sectors:", error);
    }
  }

  const fetchTagIndustries = async () => {
    try {
      const response = await educationService.getTagIndustries();
      setTagIndustries(response.data || []);
    } catch (error) {
      console.error("Error fetching industries:", error);
    }
  }

  const fetchCategories = async () => {
    try {
      const response = await educationService.getCategories();
      // Assuming response.categories based on controller
      setCategories(response.categories || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  useEffect(() => {
    fetchInternalTokens();
    fetchTagSectors();
    fetchTagIndustries();
    fetchCategories();
    fetchNotificationTypes();
  }, []);






  useEffect(() => {
    let isMounted = true;

    const fetchEducation = async () => {
      if (!id) return;

      setFetchingEducation(true);
      setEducationFetchError(null);

      try {
        const response = await educationService.getEducationById(id);

        if (!isMounted) return;

        let article;
        if (response && typeof response === 'object') {
          if (response.data) {
            article = response.data;
          } else if (response.education) {
            article = response.education;
          } else {
            article = response;
          }
        }

        if (!article || typeof article !== 'object') {
          console.error("Invalid education article data received:", response);
          setEducationFetchError("Invalid data format received from server");
          return;
        }


        // Format date for input field
        if (article.publication_date) {
          article.publication_date = article.publication_date.split("T")[0];
        }

        setIsPublished(article?.ispublished === 1);

        // Parse JSON tags if they are strings (Postgres JSON columns might come as strings or objects)
        const parseTags = (tags) => {
          if (!tags) return [];
          if (Array.isArray(tags)) return tags;
          try { return JSON.parse(tags); } catch (e) { return []; }
        };

        const tags_stocks = parseTags(article.tags_stocks || article.tag_stocks);
        const tags_sectors = parseTags(article.tags_sectors || article.tag_sectors || article.tag_sector);
        const tags_industries = parseTags(article.tags_industries || article.tag_industries || article.tag_industry);

        setFormData({
          publisher: article.publisher || "",
          title: article.title || "",
          link: article.link || "",
          publication_date: article.publication_date || "",
          publish_time: article.publish_time || "",
          brief_description: article.brief_description || "",
          education_content: article.news_content || article.learning_content || article.education_content || "",
          tags_stocks: tags_stocks,
          tags_sectors: tags_sectors,
          tags_industries: tags_industries,
          credit: article.credit || "",
          source_url: article.source_url || "",
          destination_url: article.destination_url || "",
          education_category: article.learning_topic_id || article.learning_category || article.education_category || "",
          image_url: article.image_url || null,
          ntf_type: article.ntf_type || null,
          premium: article.premium || false,
        });

        if (article.image_url) {
          setImagePreview(`${REACT_APP_API_BASE_URL}uploads/educationimages/${article.image_url}`);
        }
      } catch (error) {
        if (!isMounted) return;
        console.error("Error fetching education article:", error);
        setEducationFetchError(error.message || "Failed to load education data");
        toast.error("❌ Failed to load education data");
      } finally {
        if (isMounted) {
          setFetchingEducation(false);
        }
      }
    };

    fetchEducation();

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    if (formData.education_category && categories.length > 0) {
      const category = categories.find(cat => cat.id == formData.education_category);
      if (category) seteducationCategory(category.topic);
    }
  }, [formData.education_category, categories]);


  const categoryOptions = useMemo(() =>
    categories.map(cat => ({ label: cat.topic, value: cat.id })),
    [categories]
  );

  const stockOptions = useMemo(() =>
    internalTokens.map(token => ({ label: `${token.symbol} (${token.name})`, value: token.token })),
    [internalTokens]
  );

  const sectorOptions = useMemo(() =>
    tagSectors.map(item => ({ label: item.sector_name || item, value: item.sector_name || item })),
    [tagSectors]
  );

  const industryOptions = useMemo(() =>
    tagIndustries.map(item => ({ label: item.industry_name || item, value: item.industry_name || item })),
    [tagIndustries]
  );

  const ntfOptions = useMemo(() =>
    notificationTypes.map(item => ({ label: item.query_title || "", value: item.id })),
    [notificationTypes]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.education_category) {
      toast.error("Please select a education category");
      return;
    }

    const action = e.nativeEvent.submitter?.value || "update";
    const isPublish = action === "publish";

    const data = new FormData();

    // Convert tags to JSON array safely
    const convertToJSONArray = (value) => {
      if (!value) return "[]";

      // If value is already an array, return directly
      if (Array.isArray(value)) {
        return JSON.stringify(value.filter(item => item !== ""));
      }

      // If it is a string, split it
      return JSON.stringify(
        value
          .split(",")
          .map(item => item.trim())
          .filter(item => item !== "")
      );
    };


    data.append("publisher", formData.publisher);
    data.append("title", formData.title);
    data.append("link", formData.link);
    data.append("publication_date", formData.publication_date);
    data.append("publish_time", formData.publish_time);
    data.append("brief_description", formData.brief_description);
    data.append("news_content", normalizeHtml(formData.education_content));
    data.append("learning_topic_id", formData.education_category);
    data.append("credit", formData.credit);
    data.append("source_url", formData.source_url);
    data.append("destination_url", formData.destination_url);
    data.append("premium", formData.premium || false);

    data.append("tags_stocks", convertToJSONArray(formData.tags_stocks));
    data.append("tags_sectors", convertToJSONArray(formData.tags_sectors));
    data.append("tags_industries", convertToJSONArray(formData.tags_industries));

    if (isPublish) {
      const userId = localStorage.getItem("userId");
      data.append("published_by", userId);
      data.append("ispublished", 1);
    }

    if (formData.image_url instanceof File) {
      data.append("image_url", formData.image_url);
    } else if (typeof formData.image_url === "string") {
      data.append("image_url", formData.image_url);
    }

    try {
      // Safety check: if id is the string "undefined", treat it as no ID (create mode)
      const safeId = (id === "undefined" || !id) ? null : id;

      if (safeId) {
        await educationService.updateEducation(safeId, data);
        toast.success(
          isPublish
            ? "Education published successfully"
            : "Education updated successfully"
        );
      } else {
        await educationService.createEducation(data);
        toast.success(
          isPublish
            ? "Education created and published successfully"
            : "Education created successfully"
        );
      }

      if (isPublish && formData.ntf_type) {
        // Send notification for selected type
        const ntfType = notificationTypes.find(t => t.id === formData.ntf_type);
        if (ntfType) {
          await handleSendNotification(
            1, // Default ID for education notification
            `${educationCategory} Update`,
            formData.title,
            ntfType.id,
            4 // Notification Type ID for education
          );
        }
      }
      if (onSubmitted) onSubmitted();
    } catch (error) {
      // api.js returns a formatted error object with { message, status, data }
      const msg = error.message || "Error posting education";
      toast.error("❌ " + msg);
    }
  };

  // const publishHandler = async () => {
  //   try {
  //     const userId = localStorage.getItem("userId");

  //     const payload = {}
  //     payload.published_by = userId;

  //     await educationService.isPublisheducation(id, payload);
  //     toast.success("✅ education Published successfully");
  //     navigate("/educationfeed");
  //   } catch (error) {
  //     toast.error("Error publishing")
  //   }
  // }





  const handleSendNotification = async (
    id,
    title,
    message,
    query,
    notificationtypeid,
  ) => {
    if (!id) return;

    try {
      setLoading(true);

      const payload = {
        id,
        title,
        message,
        query,
        notificationtypeid,
      };

      const response = await educationService.sendNotification(payload);
      toast.success(
        response.data?.message ||
        `Notification sent successfully to ${response.sent} users!`,
      );
      return;
    } catch (error) {
      console.error("Failed to send notification:", error);
      toast.error("Failed to send notification");
    } finally {
      setLoading(false);
    }
  };

  if (id && fetchingEducation) {
    return (
      <div className="education-container">
        <div className="flex-center" style={{ fontSize: '18px', color: '#5f6368', minHeight: '60vh' }}>
          Loading education data...
        </div>
      </div>
    );
  }

  if (id && educationFetchError) {
    return (
      <div className="education-container">
        <div className="flex-center-col" style={{ minHeight: '60vh', padding: '20px', textAlign: 'center' }}>
          <h2 className="error-title">⚠️ Error Loading Education</h2>
          <p className="error-message">{educationFetchError}</p>
          <button 
            className="retry-btn"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="education-container" style={{ padding: 0, minHeight: 'auto', background: 'transparent' }}>
      <ToastContainer position="bottom-right" autoClose={2000} />

      <div className="relative-container">
        <form className="education-card" onSubmit={handleSubmit} style={{ boxShadow: 'none', padding: '10px' }}>
          {loading && <div className="loading-overlay">Sending Notification...</div>}
          <div className="image-section-container">
            <label className="section-label">Featured Image</label>
            <div className="image-section" onClick={() => document.getElementById('imageInput').click()}>
              <div className="image-preview-container">
                <img
                  src={imagePreview || DefaultImage}
                  alt="Preview"
                  className="preview-image"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = DefaultImage;
                  }}
                />
              </div>

              <input
                id="imageInput"
                type="file"
                accept="image/*"
                name="image_url"
                onChange={handleChange}
                className="image-input display-none"
              />
            </div>

            <div className="image-actions">
              <button
                type="button"
                className="media-library-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMediaLibraryOpen(true);
                }}
              >
                📁 Browse Media Library
              </button>

              {imagePreview && (
                <button
                  type="button"
                  className="clear-image-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setImagePreview(null);
                    setFormData({ ...formData, image_url: null });
                    const input = document.getElementById("imageInput");
                    if (input) input.value = "";
                  }}
                >
                  ✖ Remove Image
                </button>
              )}
            </div>
          </div>


          <div className="form-grid">
            <div className="form-group">
              <label>Category <span className="text-red"></span></label>
              <Select
                name="education_category"
                value={categoryOptions.find((option) => option.value == formData.education_category) || null}
                onChange={(selectedOption) => {
                  setFormData({
                    ...formData,
                    education_category: selectedOption ? selectedOption.value : "",
                  });
                  seteducationCategory(selectedOption ? selectedOption.label : "");
                }}
                options={categoryOptions}
                placeholder="Select Category..."
                isSearchable
                required
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
              />
            </div>

            <div className="form-group">
              <label>Publisher</label>
              <input
                name="publisher"
                value={formData.publisher}
                onChange={handleChange}
                placeholder="Enter publisher name"

              />
            </div>

            <div className="form-group">
              <label>Credit</label>
              <input
                name="credit"
                value={formData.credit}
                onChange={handleChange}
                placeholder="Credit (e.g. Author / Agency)"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Title</label>
            <input
              name="title"
              required
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter an eye-catching headline"
            />
          </div>

          <div className="form-group">
            <label>Brief Description</label>
            <textarea
              name="brief_description"
              rows="2"
              value={formData.brief_description}
              onChange={handleChange}
              placeholder="Short summary of the education..."
              required
            />
          </div>

          <div className="form-group quill-editor-group">
            <label>Full education Content</label>
            <ReactQuill
              theme="snow"
              value={formData.education_content}
              onChange={handleEditorChange}
              placeholder="Write or paste full education content here..."
              modules={{
                toolbar: [
                  [{ header: [1, 2, 3, false] }],
                  ['bold', 'italic', 'underline', 'strike', 'blockquote'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  ['link', 'image', 'video'],
                  [{ align: ['justify'] }], // ✅ FIXED
                  ['clean'],
                ],
              }}
            />
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Link</label>
              <input
                name="link"
                value={formData.link}
                onChange={handleChange}
                placeholder="Original article link"
              />
            </div>

            <div className="form-group">
              <label>Source URL</label>
              <input
                name="source_url"
                value={formData.source_url}
                onChange={handleChange}
                placeholder="Source link"
              />
            </div>

            <div className="form-group">
              <label>Destination URL</label>
              <input
                name="destination_url"
                value={formData.destination_url}
                onChange={handleChange}
                placeholder="Redirect destination"
              />
            </div>

          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>Publication Date</label>
              <input
                type="date"
                name="publication_date"
                value={formData.publication_date}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Publish Time</label>
              <input
                type="time"
                name="publish_time"
                value={formData.publish_time}
                onChange={handleChange}
                required
              />
            </div>




            <div className="form-group">
              <label>Notification Type</label>
              <Select
                name="tags_notification_type_id"
                value={ntfOptions.find((option) => option.value == formData.ntf_type) || null}
                onChange={(selectedOption) =>
                  setFormData({
                    ...formData,
                    ntf_type: selectedOption ? selectedOption.value : null,
                  })
                }
                options={ntfOptions}
                placeholder="Select Notification Type..."
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
              />
            </div>

          </div>

          <h3 className="tags-heading">Tags</h3>

          <div className="form-grid">
            <div className="form-group">
              <label>Stocks</label>
              <Select
                name="tags_stocks"
                isMulti
                value={stockOptions.filter((option) =>
                  formData.tags_stocks && Array.isArray(formData.tags_stocks) && formData.tags_stocks.includes(option.value)
                )}
                onChange={(selectedOptions) =>
                  setFormData({
                    ...formData,
                    tags_stocks: selectedOptions ? selectedOptions.map((opt) => opt.value) : [],
                  })
                }
                options={stockOptions}
                placeholder="Select Stocks..."
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
              />
            </div>
            <div className="form-group">
              <label>Sectors</label>
              <Select
                name="tags_sectors"
                isMulti
                value={sectorOptions.filter((option) =>
                  formData.tags_sectors && Array.isArray(formData.tags_sectors) && formData.tags_sectors.includes(option.value)
                )}
                onChange={(selectedOptions) =>
                  setFormData({
                    ...formData,
                    tags_sectors: selectedOptions ? selectedOptions.map((opt) => opt.value) : [],
                  })
                }
                options={sectorOptions}
                placeholder="Select Sectors..."
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
              />
            </div>
            <div className="form-group">
              <label>Industries</label>
              <Select
                name="tags_industries"
                isMulti
                value={industryOptions.filter((option) =>
                  formData.tags_industries && Array.isArray(formData.tags_industries) && formData.tags_industries.includes(option.value)
                )}
                onChange={(selectedOptions) =>
                  setFormData({
                    ...formData,
                    tags_industries: selectedOptions ? selectedOptions.map((opt) => opt.value) : [],
                  })
                }
                options={industryOptions}
                placeholder="Select Industries..."
                isSearchable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999999 }) }}
              />
            </div>

          </div>

          <div className="form-group form-group-premium">
            <label className="premium-flex">
              <input
                type="checkbox"
                id="premium"
                name="premium"
                checked={!!formData.premium}
                onChange={handlePremiumChange}
                className="premium-checkbox"
              />
              <span className="premium-label">
                Premium education
              </span>
            </label>
          </div>


          <div className="flex-space-around">
            <button type="button" className="submit-btn" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="submit-btn" >
              {id ? 'Update' : 'Save'}
            </button>
            {id && (<button type="submit" className="publish-btn" value="publish" >
              Publish
            </button>)}
          </div>
        </form>
      </div>

      <MediaLibraryModal
        isOpen={isMediaLibraryOpen}
        onClose={() => setIsMediaLibraryOpen(false)}
        onSelect={handleMediaSelect}
        folder="educationimages"
      />
    </div>
  );
};

export default EducationForm;
